/**
 * Styled / small UI primitives for the Finish-Time-Entry-New module.
 * All components are module-level or React.memo to avoid re-creation on render.
 */

import React, { useEffect, useRef } from "react";
import { Box, Switch, Tooltip, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { styled } from "@mui/material/styles";

// ─── iOS-style toggle switch ─────────────────────────────────────────────────
export const IOSSwitch = styled(Switch)(() => ({
  width: 34,
  height: 20,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(14px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "var(--ty-primary-color, #356dad)",
        opacity: 1,
        border: 0,
      },
    },
  },
  "& .MuiSwitch-thumb": { boxSizing: "border-box", width: 16, height: 16 },
  "& .MuiSwitch-track": {
    borderRadius: 20,
    backgroundColor: "#E9E9EA",
    opacity: 1,
  },
}));

// ─── Toolbar toggle switch (On Water / SN Alpha) ────────────────────────────
export function ToolbarSwitch({ label, checked, onChange, tooltip }) {
  const control = (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
      title={tooltip ? undefined : label}
    >
      <IOSSwitch
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.5,
          color: "var(--ty-secondary-color, #42566c)",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
  return tooltip ? <Tooltip title={tooltip}>{control}</Tooltip> : control;
}

// ─── Reusable count badge ───────────────────────────────────────────────────
export const CountBadge = React.memo(({ count }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: "var(--ty-primary-color, #356dad)",
      color: "#fff",
      borderRadius: "50%",
      width: 22,
      height: 22,
      fontSize: 11,
      fontWeight: 700,
      ml: 0.5,
    }}
  >
    {count}
  </Box>
));

// ─── Live clock ─────────────────────────────────────────────────────────────
// `big` = ~2x size, used for the page-heading / fullscreen clock.
export const LiveClock = React.memo(({ compact = false, big = false }) => {
  const mobile = useMediaQuery("(max-width:600px)");
  const bigMobile = big && mobile; // big clock collapses to a compact pill on phones
  const timeRef = useRef(null);
  useEffect(() => {
    // Self-aligning tick that writes STRAIGHT to the DOM node (no setState):
    // - aligned to the wall-clock second boundary, so the displayed second
    //   always matches what a capture at that instant records;
    // - bypasses React entirely, so rapid captures on big sessions can't
    //   queue the clock's update behind grid re-renders (the "clock stuck
    //   for 2s after pressing Finish" symptom).
    let id;
    const tick = () => {
      const d = new Date();
      if (timeRef.current) {
        timeRef.current.textContent = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;
      }
      id = setTimeout(tick, 1000 - d.getMilliseconds() + 5);
    };
    tick();
    return () => clearTimeout(id);
  }, []);
  const d0 = new Date();
  const t = `${String(d0.getHours()).padStart(2, "0")}:${String(d0.getMinutes()).padStart(2, "0")}:${String(d0.getSeconds()).padStart(2, "0")}`;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: bigMobile ? 0.5 : big ? 1 : 0.5,
        ml: big ? 0 : 0.5,
        bgcolor: "var(--ty-secondary-color, #42566c)",
        color: "#fff",
        borderRadius: bigMobile ? 1 : big ? 1.5 : 1,
        ...(bigMobile
          ? { pl: "3px", pr: "3px", pt: "2px", pb: "1px" }
          : { px: big ? 1.5 : compact ? 0.5 : 1, py: big ? "2px" : "2px" }),
      }}
    >
      {!compact && !bigMobile && (
        <Typography
          component="span"
          sx={{
            fontSize: big ? 14 : 9,
            fontWeight: 700,
            letterSpacing: big ? 1 : 0.5,
          }}
        >
          TIME
        </Typography>
      )}
      <Typography
        ref={timeRef}
        component="span"
        sx={{
          fontFamily: "monospace",
          fontWeight: 700,
          fontSize: bigMobile ? 14 : big ? 24 : compact ? 11 : 12,
          letterSpacing: bigMobile ? 0.5 : big ? 2 : compact ? 0.5 : 1,
        }}
      >
        {t}
      </Typography>
    </Box>
  );
});

// ─── Labeled toggle row (Retain HR / Retain Did Not) ────────────────────────
export const LabeledSwitch = React.memo(({ label, checked, onChange }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    <IOSSwitch checked={checked} onChange={(e) => onChange(e.target.checked)} />
    <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{label}</Typography>
  </Box>
));
